import { bindable, noView, processContent, autoinject, Container } from 'aurelia-framework';
import { ViewCompiler, ViewFactory, ViewSlot, ViewResources, BehaviorInstruction } from 'aurelia-templating';
import { createOverrideContext, OverrideContext } from 'aurelia-binding';
import { TemplateFragmentRegistry } from './template-fragment-registry';

/**
 * Base class for view-less elements that lift their content and compile it into a template
 * to be used later.
 */
export abstract class TemplateCompilingElement {
	protected elementContentViewFactory:ViewFactory;

	constructor(element: Element, compiler: ViewCompiler, resources: ViewResources) {
		let template = `<template>${element.innerHTML}</template>`;
		this.elementContentViewFactory = compiler.compile(template, resources);

		element.innerHTML = '';
	}
}

@noView
@processContent(false)
@autoinject
export class TemplatePlaceholder extends TemplateCompilingElement {
	@bindable overrideDefined: boolean;

	private slot:ViewSlot;
	private name: string;

	constructor(private element: Element, compiler: ViewCompiler, resources: ViewResources, private container:Container, private templates:TemplateFragmentRegistry) {
		super(element, compiler, resources);

		const placeholderName = element.getAttribute('name');

		this.name = placeholderName;
		this.slot = new ViewSlot(this.element, true);
	}


	private overrideContext: OverrideContext;
	bind(context: any, overrideContext: OverrideContext): void {
		this.overrideContext = createOverrideContext(this, overrideContext);
	}

	attached():void {
		let replacementTemplate = this.templates.get(this.name, this.element.parentElement);

		this.overrideDefined = replacementTemplate != null;

		let template = replacementTemplate || this.elementContentViewFactory;

		let view = template.create(this.container);

		this.slot.add(view);
		this.slot.bind(this, createOverrideContext(this, this.overrideContext));
		this.slot.attached();
	}

	unbind():void {
		this.slot.unbind();
	}

	detached():void {
		this.slot.removeAll();
	}
}

export abstract class TemplateSupport {
	private registry: TemplateFragmentRegistry;

	constructor(private rootElement: Element, container: Container, compiler: ViewCompiler, resources: ViewResources) {
		this.registry = <TemplateFragmentRegistry>container.get(TemplateFragmentRegistry);

		for (let i = 0; i < rootElement.childNodes.length; i++) {
			const templateElement = <Element>rootElement.childNodes.item(i);
			if (templateElement.tagName !== 'TEMPLATE-CONTENT') {
				continue;
			}

			rootElement.removeChild(templateElement);
			const targetName = templateElement.getAttribute('target-name');
			const template = `<template>${templateElement.innerHTML}</template>`;

			const elementContentViewFactory = compiler.compile(template, resources);

			this.registry.register(targetName, elementContentViewFactory, rootElement);
		}
	}

	public isTemplateDefined(name: string): boolean {
		return this.registry.isDefined(name, this.rootElement);
	}
}

export function inheritBindingContext(continueProcessing: boolean) {
	return (compiler: ViewCompiler,
		resources: ViewResources,
		node: Element,
		instruction: BehaviorInstruction) => {
		instruction.inheritBindingContext = true;
		return continueProcessing;
	};
}
